import { href, Link } from 'react-router'
import type { loader as layoutLoader } from '~/routes/_layout'
import { useShopContext } from '~/utils/use-shop-context'
import type { MetaFunction } from 'react-router'

export const meta: MetaFunction<Record<string, never>, { layout: typeof layoutLoader }> = ({ matches }) => {
  const layoutData = matches.find(m => m.id === 'layout')?.data
  const companyName = layoutData?.orgDetails?.companyName ?? 'Webshop'
  const canonicalUrl = layoutData?.canonicalUrl ? `${layoutData.canonicalUrl}/privacy-policy` : undefined

  const title = `Privacybeleid | ${companyName}`
  const description =
    'Lees ons privacybeleid over hoe wij omgaan met uw persoonlijke gegevens conform de AVG. Informatie over cookies, bewaartermijnen en uw rechten.'

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    url: canonicalUrl,
    inLanguage: 'nl-NL',
    author: { '@type': 'Organization', name: companyName },
    publisher: { '@type': 'Organization', name: companyName },
    dateModified: '2025-01-01'
  }

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:locale', content: 'nl_NL' },
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'robots', content: 'index, follow' },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
    { 'script:ld+json': articleJsonLd }
  ]
}

export default function PrivacyPolicy() {
  const { orgDetails: od } = useShopContext()

  return (
    <div className="mx-auto w-full max-w-3xl flex-1 p-4 lg:p-2 lg:pt-4">
      <div className="rounded-lg bg-white p-6 shadow-sm sm:p-8">
        <h1 className="mb-6 text-2xl font-bold text-gray-900">Privacybeleid</h1>

        <p className="mb-6 text-sm text-gray-500">Laatst bijgewerkt: januari 2025</p>

        <div className="space-y-8 text-sm leading-relaxed text-gray-700">
          {/* Inleiding */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">1. Inleiding</h2>
            <p>
              {od?.companyName} (hierna: "wij", "ons" of "onze") respecteert uw privacy en zet zich in voor de bescherming van uw persoonsgegevens.
              Dit privacybeleid informeert u over hoe wij omgaan met uw persoonsgegevens wanneer u onze website bezoekt en vertelt u over uw
              privacyrechten en hoe de wet u beschermt.
            </p>
          </section>

          {/* Contactgegevens */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">2. Wie zijn wij?</h2>
            <p className="mb-2">Wij zijn de verwerkingsverantwoordelijke voor uw persoonsgegevens. U kunt ons bereiken via:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>
                <strong>Bedrijfsnaam:</strong> {od?.companyName}
              </li>
              <li>
                <strong>Adres:</strong> {od?.street} {od?.houseNumber}, {od?.postalCode} {od?.city}
              </li>
              <li>
                <strong>KVK-nummer:</strong> {od?.kvkNumber}
              </li>
              <li>
                <strong>Telefoon:</strong> {od?.telephone}
              </li>
            </ul>
          </section>

          {/* Welke gegevens verzamelen wij */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">3. Welke persoonsgegevens verzamelen wij?</h2>
            <p className="mb-3">Wij kunnen de volgende categorieën persoonsgegevens over u verzamelen:</p>

            <h3 className="mb-2 font-medium text-gray-900">Identiteitsgegevens</h3>
            <p className="mb-3">Voornaam, achternaam, bedrijfsnaam (indien van toepassing).</p>

            <h3 className="mb-2 font-medium text-gray-900">Contactgegevens</h3>
            <p className="mb-3">E-mailadres, telefoonnummer, factuur- en afleveradres.</p>

            <h3 className="mb-2 font-medium text-gray-900">Zakelijke gegevens</h3>
            <p className="mb-3">KVK-nummer en BTW-nummer (voor zakelijke klanten).</p>

            <h3 className="mb-2 font-medium text-gray-900">Transactiegegevens</h3>
            <p className="mb-3">Details over bestellingen, betalingen en leveringen.</p>

            <h3 className="mb-2 font-medium text-gray-900">Technische gegevens</h3>
            <p>IP-adres, browsertype, tijdzone, besturingssysteem (alleen indien u toestemming geeft voor analytische cookies).</p>
          </section>

          {/* Hoe verzamelen wij gegevens */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">4. Hoe verzamelen wij uw gegevens?</h2>
            <p className="mb-3">Wij verzamelen gegevens via:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>
                <strong>Directe interacties:</strong> Wanneer u een bestelling plaatst, een account aanmaakt of contact met ons opneemt.
              </li>
              <li>
                <strong>Geautomatiseerde technologieën:</strong> Via cookies en vergelijkbare technologieën (alleen met uw toestemming voor
                niet-essentiële cookies).
              </li>
            </ul>
          </section>

          {/* Waarvoor gebruiken wij gegevens */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">5. Waarvoor gebruiken wij uw gegevens?</h2>
            <p className="mb-3">Wij gebruiken uw persoonsgegevens voor:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>Het verwerken en leveren van uw bestellingen</li>
              <li>Het beheren van betalingen en facturatie</li>
              <li>Het versturen van orderbevestigingen en verzendnotificaties</li>
              <li>Het behandelen van vragen en klachten</li>
              <li>Het verbeteren van onze website en diensten (met uw toestemming)</li>
              <li>Het voldoen aan wettelijke verplichtingen</li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">6. Cookies en vergelijkbare technologieën</h2>
            <p className="mb-3">
              Wij gebruiken cookies om onze website te laten functioneren en om uw gebruikerservaring te verbeteren. U kunt uw cookievoorkeuren
              beheren via onze{' '}
              <Link to={href('/cookie-instellingen')} className="text-(--ws-link) underline hover:no-underline">
                cookie-instellingenpagina
              </Link>
              .
            </p>

            <h3 className="mb-2 font-medium text-gray-900">Essentiële cookies (altijd actief)</h3>
            <ul className="mb-3 ml-4 list-disc space-y-1">
              <li>Winkelwagenfunctionaliteit (bewaartermijn: 30 dagen)</li>
              <li>Authenticatie en sessiemanagement (via Clerk)</li>
              <li>Cookie-voorkeursinstellingen (bewaartermijn: 1 jaar)</li>
            </ul>

            <h3 className="mb-2 font-medium text-gray-900">Functionele cookies (optioneel)</h3>
            <ul className="mb-3 ml-4 list-disc space-y-1">
              <li>Onthouden van formuliergegevens bij afrekenen (bewaartermijn: 1 jaar)</li>
            </ul>

            <h3 className="mb-2 font-medium text-gray-900">Analytische cookies (optioneel)</h3>
            <ul className="ml-4 list-disc space-y-1">
              <li>PostHog analytics - voor het analyseren van websitegebruik</li>
            </ul>
          </section>

          {/* Externe dienstverleners */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">7. Externe dienstverleners</h2>
            <p className="mb-3">
              Deze webshop maakt gebruik van het WeUniteBrands platform. WeUniteBrands B.V. treedt op als verwerker en beheert de technische
              infrastructuur namens {od?.companyName}. Via dit platform worden de volgende sub-verwerkers ingezet:
            </p>

            <div className="space-y-3">
              <div>
                <h3 className="font-medium text-gray-900">WeUniteBrands B.V. (platform verwerker)</h3>
                <p className="text-gray-500">
                  Beheert het webshop platform, de database en alle onderliggende technische diensten. WeUniteBrands is gevestigd in Nederland en
                  verwerkt gegevens conform de AVG. Contactgegevens: info@weunitebrands.nl
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Clerk (authenticatie)</h3>
                <p className="text-gray-500">Voor het beheren van gebruikersaccounts en inloggen. Clerk verwerkt gegevens conform de AVG.</p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Mailtrap (e-mail)</h3>
                <p className="text-gray-500">Voor het versturen van orderbevestigingen, facturen en transactionele e-mails.</p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Cloudinary (beeldoptimalisatie)</h3>
                <p className="text-gray-500">Voor het optimaliseren en leveren van productafbeeldingen.</p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Vercel (hosting)</h3>
                <p className="text-gray-500">Voor het hosten van de webshop applicatie.</p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Neon (database)</h3>
                <p className="text-gray-500">Voor het veilig opslaan van klant- en ordergegevens. Gegevens worden versleuteld opgeslagen.</p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">PostHog (analytics)</h3>
                <p className="text-gray-500">
                  Voor het analyseren van websitegebruik. Wordt alleen geactiveerd met uw toestemming. Gegevens worden opgeslagen in de EU
                  (eu.posthog.com).
                </p>
              </div>
            </div>
          </section>

          {/* Bewaartermijnen */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">8. Hoe lang bewaren wij uw gegevens?</h2>
            <ul className="ml-4 list-disc space-y-1">
              <li>
                <strong>Ordergegevens:</strong> 7 jaar (wettelijke bewaarplicht)
              </li>
              <li>
                <strong>Winkelwagengegevens:</strong> 30 dagen na laatste activiteit
              </li>
              <li>
                <strong>Formuliergegevens:</strong> 1 jaar (lokaal op uw apparaat)
              </li>
              <li>
                <strong>Analytische gegevens:</strong> Conform het beleid van PostHog
              </li>
            </ul>
          </section>

          {/* Uw rechten */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">9. Uw rechten</h2>
            <p className="mb-3">Onder de AVG heeft u de volgende rechten:</p>
            <ul className="ml-4 list-disc space-y-1">
              <li>
                <strong>Recht op inzage:</strong> U kunt opvragen welke gegevens wij van u verwerken.
              </li>
              <li>
                <strong>Recht op rectificatie:</strong> U kunt onjuiste gegevens laten corrigeren.
              </li>
              <li>
                <strong>Recht op verwijdering:</strong> U kunt vragen om uw gegevens te verwijderen.
              </li>
              <li>
                <strong>Recht op beperking:</strong> U kunt de verwerking van uw gegevens laten beperken.
              </li>
              <li>
                <strong>Recht op overdraagbaarheid:</strong> U kunt uw gegevens in een gangbaar formaat ontvangen.
              </li>
              <li>
                <strong>Recht van bezwaar:</strong> U kunt bezwaar maken tegen de verwerking van uw gegevens.
              </li>
            </ul>
            <p className="mt-3">Om uw rechten uit te oefenen, kunt u contact met ons opnemen via de contactgegevens in sectie 2.</p>
          </section>

          {/* Beveiliging */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">10. Beveiliging</h2>
            <p>
              Wij hebben passende technische en organisatorische maatregelen getroffen om uw persoonsgegevens te beveiligen tegen verlies of
              onrechtmatige verwerking. Dit omvat onder andere versleutelde verbindingen (HTTPS), beveiligde databases met TLS-encryptie, en beperkte
              toegang tot persoonsgegevens.
            </p>
          </section>

          {/* Wijzigingen */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">11. Wijzigingen in dit privacybeleid</h2>
            <p>
              Wij kunnen dit privacybeleid van tijd tot tijd bijwerken. Wijzigingen worden op deze pagina gepubliceerd met een bijgewerkte "laatst
              bijgewerkt" datum. Wij raden u aan dit beleid regelmatig te raadplegen.
            </p>
          </section>

          {/* Klachten */}
          <section>
            <h2 className="mb-3 text-lg font-semibold text-gray-900">12. Klachten</h2>
            <p>
              Indien u een klacht heeft over de verwerking van uw persoonsgegevens, kunt u contact met ons opnemen. U heeft ook het recht om een
              klacht in te dienen bij de Autoriteit Persoonsgegevens (
              <a
                href="https://autoriteitpersoonsgegevens.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="text-(--ws-link) underline hover:no-underline">
                www.autoriteitpersoonsgegevens.nl
              </a>
              ).
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
